/**
 * SVG path for a coach-mark highlight that hugs two stacked boxes — a trigger
 * and the panel hanging off it — with one continuous outline instead of their
 * plain bounding box (Figma 9430:3044, the task "more actions" step).
 *
 * `rect` is the anchor rect produced by `notchedUnionRects`: the union box plus
 * `parts: [trigger, panel]` in viewport coordinates. The path is expressed in
 * the local coordinates of the highlight element, whose top-left sits `offset`
 * px outside the union box, and its stroke centre is inset by half the stroke
 * width so the outer edge of the stroke lands exactly where the plain
 * bordered highlight's edge would.
 */

/** Stroke width of the outline; keep in sync with `.walkthrough-highlight__path`. */
export const NOTCHED_OUTLINE_STROKE = 2;
/** Corner radius of the outline; matches `--dimension-border-radius-04` (4px). */
export const NOTCHED_OUTLINE_RADIUS = 4;

const round = (value) => Math.round(value * 100) / 100;
const point = ([x, y]) => `${round(x)} ${round(y)}`;

/** Edges of one part's inflated box, in the highlight's local coordinates, at the stroke centre. */
const edgesOf = (part, rect, offset, half) => ({
    top: part.top - rect.top + half,
    left: part.left - rect.left + half,
    right: part.left + part.width - rect.left + offset * 2 - half,
    bottom: part.top + part.height - rect.top + offset * 2 - half,
});

const unit = (from, to) => {
    const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    return length ? [(to[0] - from[0]) / length, (to[1] - from[1]) / length, length] : [0, 0, 0];
};

/** Closed path through `points` (an axis-aligned polygon) with every corner rounded by up to `radius`. */
const roundedPolygonPath = (points, radius) => {
    const count = points.length;
    const commands = points.map((vertex, index) => {
        const prev = points[(index - 1 + count) % count];
        const next = points[(index + 1) % count];
        const [inX, inY, inLength] = unit(prev, vertex);
        const [outX, outY, outLength] = unit(vertex, next);
        const r = Math.min(radius, inLength / 2, outLength / 2);
        const start = [vertex[0] - inX * r, vertex[1] - inY * r];
        const end = [vertex[0] + outX * r, vertex[1] + outY * r];
        return `${index === 0 ? 'M' : 'L'}${point(start)} Q${point(vertex)} ${point(end)}`;
    });
    return `${commands.join(' ')} Z`;
};

/** Clockwise corners of the outline around the upper box `a` and the lower box `b`. */
const notchedCorners = (a, b) => {
    const corners = [
        [a.left, a.top],
        [a.right, a.top],
    ];
    if (b.right > a.right) corners.push([a.right, b.top], [b.right, b.top]);
    else if (b.right < a.right) corners.push([a.right, a.bottom], [b.right, a.bottom]);
    corners.push([b.right, b.bottom], [b.left, b.bottom]);
    if (b.left < a.left) corners.push([b.left, b.top], [a.left, b.top]);
    else if (b.left > a.left) corners.push([b.left, a.bottom], [a.left, a.bottom]);
    return corners;
};

/**
 * Path data for the notched outline, or `null` when `rect` has no two stacked
 * `parts` (the caller then falls back to the plain bordered highlight).
 */
export const notchedOutlinePath = (rect, offset, radius = NOTCHED_OUTLINE_RADIUS) => {
    if (!Array.isArray(rect?.parts) || rect.parts.length !== 2) return null;
    const half = NOTCHED_OUTLINE_STROKE / 2;
    const [a, b] = [...rect.parts]
        .sort((first, second) => first.top - second.top)
        .map((part) => edgesOf(part, rect, offset, half));
    if (!(b.top > a.top && b.bottom > a.bottom)) return null;
    return roundedPolygonPath(notchedCorners(a, b), radius);
};
